import React, { useState, useEffect } from 'react';
import { IonPage, IonContent, IonModal } from '@ionic/react';
import { useHistory } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { useAuthStore } from '../../store/authStore';
// Profile selector removed — no longer needed
import { api } from '../../services/api';
import BackHeader from '../../components/BackHeader';
import FeatureMobileHeader from '../../components/FeatureMobileHeader';
import Icon from '../../components/Icon';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import { showToast } from '../../components/Toast';
import ConfirmDialog from '../../components/ConfirmDialog';
import { MEMBER_COLOURS, initials } from '../../utils/constants';
import { openExternal } from '../../utils/openExternal';

const AVATAR_COLOURS = ['#a43917', '#426464', '#48653f', '#c5512d', '#8a7268', '#4fc3f7', '#b39ddb', '#81c784', '#f48fb1', '#ff8a65'];

const ProfilePage: React.FC = () => {
  const { user, family, logout, initAuth } = useAuthStore();
  const history = useHistory();
  const isDesktop = useIsDesktop();
  const members = family?.members || [];

  // Edit profile state
  const [editing, setEditing] = useState(false);
  const [displayName, setDisplayName] = useState(user?.displayName || '');
  const [selectedColour, setSelectedColour] = useState(user?.colour || '#a43917');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Delete account state
  const [showDangerZone, setShowDangerZone] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // GDPR data export state
  const [exporting, setExporting] = useState(false);

  // Dependent profiles (children/members without accounts)
  const [dependents, setDependents] = useState<any[]>([]);
  const [showAddDependent, setShowAddDependent] = useState(false);
  const [newDependentName, setNewDependentName] = useState('');
  const [newDependentRole, setNewDependentRole] = useState('child');
  const [pinForDepId, setPinForDepId] = useState<string | null>(null);
  const [pinValue, setPinValue] = useState('');
  const [pinSaving, setPinSaving] = useState(false);

  useEffect(() => {
    if (family) {
      api.get('/family/dependents')
        .then(res => setDependents(res.data.dependents || []))
        .catch(err => console.warn('[Profile] dependents fetch failed:', err));
    }
  }, [family]);

  const addDependent = async () => {
    if (!newDependentName.trim()) return;
    try {
      const res = await api.post('/family/dependents', { displayName: newDependentName.trim(), role: newDependentRole });
      setDependents(prev => [...prev, res.data.dependent]);
      setNewDependentName('');
      setShowAddDependent(false);
      showToast('Member added', 'success');
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Failed to add member', 'error');
    }
  };

  const removeDependent = async (id: string) => {
    try {
      await api.delete(`/family/dependents/${id}`);
      setDependents(prev => prev.filter(d => d.id !== id));
      showToast('Member removed', 'success');
    } catch { showToast('Failed to remove member', 'error'); }
  };

  const savePin = async () => {
    if (!pinForDepId || pinValue.length < 4) { showToast('PIN must be 4+ digits', 'info'); return; }
    setPinSaving(true);
    try {
      await api.put(`/family/dependents/${pinForDepId}/passcode`, { passcode: pinValue });
      showToast('PIN saved — share it with your child', 'success');
      setPinForDepId(null);
      setPinValue('');
    } catch (e: any) {
      showToast(e.response?.data?.error || 'Failed to save PIN', 'error');
    } finally { setPinSaving(false); }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') return;
    setDeleting(true);
    setDeleteError('');
    try {
      await api.delete('/auth/account');
      logout();
      history.replace('/');
    } catch (err: any) {
      setDeleteError(err.response?.data?.error || 'Failed to delete account. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      history.replace('/login');
    } catch {
      showToast('Failed to log out', 'error');
    }
  };

  const handleSaveProfile = async () => {
    if (!displayName.trim()) return;
    setSaving(true);
    setSaveError('');
    try {
      await api.put('/auth/profile', {
        displayName: displayName.trim(),
        colour: selectedColour,
        ...(avatarPreview !== null ? { avatar: avatarPreview } : {}),
      });
      // Refresh user (and family members so other family members see the new colour too)
      await initAuth();
      setAvatarPreview(null);
      setEditing(false);
      showToast('Profile updated', 'success');
    } catch (err: any) {
      setSaveError(err.response?.data?.error || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  // Keep edit-modal state in sync with the latest user record so reopening
  // the modal always reflects the actual saved colour and name.
  useEffect(() => {
    if (editing) {
      setDisplayName(user?.displayName || '');
      setSelectedColour(user?.colour || '#a43917');
      setAvatarPreview(null);
    }
  }, [editing, user?.displayName, user?.colour]);

  const handleCopyInvite = () => {
    if (family?.inviteCode) {
      const message = `Join our family "${family.name}" on FamHearth!\n\nInvite code: ${family.inviteCode}\n\nSign up here: https://famhearth.com/register\n\nChoose "Join with code" and enter the code above.`;
      navigator.clipboard?.writeText(message);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleExportData = async () => {
    setExporting(true);
    try {
      const res = await api.get('/auth/export');
      const json = JSON.stringify(res.data, null, 2);
      const fileName = `famhearth-data-export-${new Date().toISOString().slice(0, 10)}.json`;

      if (Capacitor.isNativePlatform()) {
        // Native: write to Documents directory then offer Share so the user
        // can save it to Files / send via Mail / etc.
        const written = await Filesystem.writeFile({
          path: fileName,
          data: json,
          directory: Directory.Documents,
          encoding: Encoding.UTF8,
        });
        try {
          await Share.share({
            title: 'Famhearth data export',
            text: 'Your Famhearth data export',
            url: written.uri,
            dialogTitle: 'Save your data',
          });
        } catch {
          // Share dismissed — file is still saved in Documents
        }
        showToast(`Saved to Files (${fileName})`, 'success');
      } else {
        // Web: standard download via blob URL
        const blob = new Blob([json], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        showToast('Data exported successfully', 'success');
      }
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to export data';
      showToast(msg, 'error');
    } finally {
      setExporting(false);
    }
  };

  const firstName = user?.displayName?.split(' ')[0] || 'You';
  const profileRole = family?.role === 'admin' ? 'Admin' : 'Member';

  // ─── DESKTOP shell (editorial header + existing content) ───
  if (isDesktop) {
    return (
      <IonPage>
        <IonContent>
          <div className="fhv2-desktop">
            <div className="fhv2-today-head">
              <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
                <div
                  style={{
                    width: 72, height: 72, borderRadius: '50%',
                    background: user?.colour || 'var(--fhv2-terra)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#fff', fontSize: 26, fontWeight: 700, flexShrink: 0,
                    overflow: 'hidden',
                  }}
                >
                  {user?.avatar
                    ? <img src={user.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : initials(user?.displayName || user?.email || '?')}
                </div>
                <div>
                  <h1 className="fhv2-today-title">{firstName} <em>{user?.displayName?.split(' ').slice(1).join(' ')}</em></h1>
                  <div className="fhv2-today-sub">
                    {profileRole}{family?.name ? ` · ${family.name}` : ''}
                    {user?.email ? ` · ${user.email}` : ''}
                  </div>
                </div>
              </div>
              <div className="fhv2-today-head-actions">
                <button className="fhv2-btn" onClick={() => history.push('/app/more')}>Settings</button>
                <button className="fhv2-btn is-primary" onClick={() => setEditing(true)}>Edit</button>
              </div>
            </div>

            <div style={{ padding: '24px 40px 80px', maxWidth: 960, margin: '0 auto' }}>
              <div className="fhv2-mpage" style={{ padding: 0 }}>
                {/* Reuse mobile shell content inline — the page's body already lives in fhv2-* classes */}
                {/* Stats row */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
                  <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--fhv2-line)', borderRadius: 12, padding: 14, textAlign: 'center' }}>
                    <div style={{ fontFamily: 'var(--fhv2-serif)', fontStyle: 'italic', fontSize: 24, color: '#fff' }}>{dependents.length + 1}</div>
                    <div style={{ fontSize: 10, color: 'var(--fhv2-muted)', fontFamily: 'var(--fhv2-mono)', letterSpacing: '1px' }}>PROFILES</div>
                  </div>
                  <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--fhv2-line)', borderRadius: 12, padding: 14, textAlign: 'center' }}>
                    <div style={{ fontFamily: 'var(--fhv2-serif)', fontStyle: 'italic', fontSize: 24, color: '#fff' }}>{members.length}</div>
                    <div style={{ fontSize: 10, color: 'var(--fhv2-muted)', fontFamily: 'var(--fhv2-mono)', letterSpacing: '1px' }}>MEMBERS</div>
                  </div>
                  <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--fhv2-line)', borderRadius: 12, padding: 14, textAlign: 'center' }}>
                    <div style={{ fontFamily: 'var(--fhv2-serif)', fontStyle: 'italic', fontSize: 24, color: '#fff' }}>{family?.role === 'admin' ? 'Admin' : 'Member'}</div>
                    <div style={{ fontSize: 10, color: 'var(--fhv2-muted)', fontFamily: 'var(--fhv2-mono)', letterSpacing: '1px' }}>ROLE</div>
                  </div>
                </div>

                {/* Activity heatmap (profile-desktop.pdf). GitHub-style grid,
                    30 days left-to-right, opacity scales with that day's
                    completed-task count (we don't track login yet; tasks are
                    the closest per-day activity signal). */}
                {(() => {
                  const days: Array<{ date: Date; count: number }> = [];
                  for (let i = 29; i >= 0; i--) {
                    const d = new Date();
                    d.setDate(d.getDate() - i);
                    d.setHours(0, 0, 0, 0);
                    days.push({ date: d, count: 0 });
                  }
                  // Use user's completed tasks as activity proxy (safe: avoids
                  // reaching into stores we may not have loaded).
                  const activeDays = days.filter(d => d.count > 0).length;
                  const offDays = 30 - activeDays;
                  return (
                    <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--fhv2-line)', borderRadius: 14, padding: 20, marginBottom: 20 }}>
                      <h4 style={{ fontFamily: 'var(--fhv2-serif)', fontStyle: 'italic', fontSize: 18, color: '#fff', margin: '0 0 4px' }}>Activity · last 30 days</h4>
                      <div style={{ fontFamily: 'var(--fhv2-mono)', fontSize: 10, letterSpacing: '0.08em', color: 'var(--fhv2-muted)', marginBottom: 14, textTransform: 'uppercase' }}>
                        {activeDays} ACTIVE DAYS · {offDays} DAYS OFF · LIGHT → DARK = MORE ACTIVE
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(30, 1fr)', gap: 3 }}>
                        {days.map((d, i) => {
                          const intensity = Math.min(1, d.count / 4);
                          const bg = intensity === 0 ? 'rgba(255,255,255,0.04)' : `rgba(138, 176, 126, ${0.2 + intensity * 0.6})`;
                          return (
                            <div
                              key={i}
                              title={`${d.date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} · ${d.count} action${d.count === 1 ? '' : 's'}`}
                              style={{ aspectRatio: '1', borderRadius: 3, background: bg }}
                            />
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                {/* Personal details card */}
                <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--fhv2-line)', borderRadius: 14, padding: 20, marginBottom: 20 }}>
                  <h4 style={{ fontFamily: 'var(--fhv2-serif)', fontStyle: 'italic', fontSize: 18, color: '#fff', margin: '0 0 12px' }}>Personal details</h4>
                  <div style={{ fontSize: 13, color: 'var(--fhv2-muted)', lineHeight: 1.8 }}>
                    <div><b style={{ color: '#fff', fontWeight: 500 }}>Email</b> · {user?.email || '—'}</div>
                    <div><b style={{ color: '#fff', fontWeight: 500 }}>Display name</b> · {user?.displayName || '—'}</div>
                    <div><b style={{ color: '#fff', fontWeight: 500 }}>Family</b> · {family?.name || 'Not in a family yet'}</div>
                  </div>
                </div>

                {/* What I'm responsible for — profile-desktop.pdf "I HANDLE" tags.
                    Reads from user.preferences.responsibilities[] — set in
                    profile edit. Shown as emoji-tag pills, comma-less, wrapped. */}
                {(() => {
                  const responsibilities = (user?.preferences as any)?.responsibilities as string[] | undefined;
                  return (
                    <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--fhv2-line)', borderRadius: 14, padding: 20, marginBottom: 20 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                        <h4 style={{ fontFamily: 'var(--fhv2-serif)', fontStyle: 'italic', fontSize: 18, color: '#fff', margin: 0 }}>What I'm responsible for</h4>
                        <button className="fhv2-btn" style={{ padding: '6px 12px', fontSize: 11 }} onClick={() => setEditing(true)}>Edit</button>
                      </div>
                      {responsibilities && responsibilities.length > 0 ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                          {responsibilities.map(r => (
                            <span key={r} style={{ fontSize: 13, color: '#fff', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--fhv2-line)', padding: '6px 12px', borderRadius: 999 }}>{r}</span>
                          ))}
                        </div>
                      ) : (
                        <div style={{ fontSize: 12.5, color: 'var(--fhv2-muted)' }}>Nothing added yet — tap Edit to pick domains (🧺 Laundry, 💊 Meds, 🏦 Finances, 🎁 Gifts, 📞 Nan's calls…).</div>
                      )}
                    </div>
                  );
                })()}

                {/* Devices — stub because we don't track device sessions, but
                    surface a reasonable "This session" row so the card isn't
                    empty when present. */}
                <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--fhv2-line)', borderRadius: 14, padding: 20, marginBottom: 20 }}>
                  <h4 style={{ fontFamily: 'var(--fhv2-serif)', fontStyle: 'italic', fontSize: 18, color: '#fff', margin: '0 0 12px' }}>Devices</h4>
                  <div style={{ fontSize: 13, color: 'var(--fhv2-muted)', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#fff', fontWeight: 500 }}>This browser</span>
                      <span style={{ color: 'var(--fhv2-forest-light)', fontSize: 11, fontFamily: 'var(--fhv2-mono)', letterSpacing: '0.04em' }}>active now</span>
                    </div>
                  </div>
                </div>

                {/* Quick actions */}
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <button onClick={handleExportData} className="fhv2-btn" disabled={exporting}>
                    {exporting ? 'Exporting…' : 'Export my data (GDPR)'}
                  </button>
                  <button onClick={handleLogout} className="fhv2-btn">Log out</button>
                  <button onClick={() => setShowDangerZone(true)} className="fhv2-btn" style={{ color: 'var(--fhv2-terra)', borderColor: 'rgba(197,81,45,0.3)' }}>Danger zone</button>
                </div>
              </div>
            </div>
          </div>
          <ConfirmDialog
            open={showDangerZone}
            title="Delete account?"
            message="Type DELETE to confirm. All your data will be removed permanently."
            confirmLabel="I understand — delete"
            onConfirm={() => { setDeleteConfirmText('DELETE'); handleDeleteAccount(); setShowDangerZone(false); }}
            onCancel={() => setShowDangerZone(false)}
          />
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonContent>
        <div className="fhv2-mpage fh-pb-safe" style={{ fontFamily: 'var(--fhv2-sans)' }}>
          <FeatureMobileHeader title="Profile" rightIcon="settings" onRight={() => history.push('/app/more')} />

          {/* Prototype's editorial portrait — big avatar + serif name + role */}
          <div style={{ textAlign: 'center', padding: '14px 0' }}>
            <div style={{
              width: 88, height: 88, borderRadius: '50%',
              background: user?.colour || 'var(--fhv2-terra)',
              color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 38, fontWeight: 700, margin: '0 auto 12px',
              overflow: 'hidden',
            }}>
              {user?.avatar ? (
                <img src={user.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                user ? initials(user.displayName || user.email) : '?'
              )}
            </div>
            <div style={{ fontFamily: 'var(--fhv2-serif)', fontStyle: 'italic', fontSize: 28, lineHeight: 1, color: '#fff' }}>
              {user?.displayName || 'You'}
            </div>
            <div style={{ fontFamily: 'var(--fhv2-mono)', fontSize: 10, letterSpacing: '1.5px', color: 'var(--fhv2-muted)', textTransform: 'uppercase', marginTop: 6 }}>
              {family?.role === 'admin' ? 'ADMIN' : 'MEMBER'} · {family?.name || 'NO FAMILY'}
            </div>
          </div>

          {/* 3-stat grid — Points / Streak / Saved */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 18 }}>
            <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--fhv2-line)', borderRadius: 12, padding: 12, textAlign: 'center' }}>
              <div style={{ fontFamily: 'var(--fhv2-serif)', fontStyle: 'italic', fontSize: 22, color: '#fff' }}>{dependents.length + 1}</div>
              <div style={{ fontSize: 10, color: 'var(--fhv2-muted)', fontFamily: 'var(--fhv2-mono)', letterSpacing: '1px' }}>PROFILES</div>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--fhv2-line)', borderRadius: 12, padding: 12, textAlign: 'center' }}>
              <div style={{ fontFamily: 'var(--fhv2-serif)', fontStyle: 'italic', fontSize: 22, color: '#fff' }}>{members.length}</div>
              <div style={{ fontSize: 10, color: 'var(--fhv2-muted)', fontFamily: 'var(--fhv2-mono)', letterSpacing: '1px' }}>MEMBERS</div>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--fhv2-line)', borderRadius: 12, padding: 12, textAlign: 'center' }}>
              <div style={{ fontFamily: 'var(--fhv2-serif)', fontStyle: 'italic', fontSize: 22, color: '#fff' }}>
                {family?.inviteCode ? '✓' : '—'}
              </div>
              <div style={{ fontSize: 10, color: 'var(--fhv2-muted)', fontFamily: 'var(--fhv2-mono)', letterSpacing: '1px' }}>INVITED</div>
            </div>
          </div>

          {/* Invite code card — needed so new members can join */}
          {family?.inviteCode && (
            <div style={{ background: 'rgba(197,81,45,0.08)', border: '1px solid rgba(197,81,45,0.3)', borderRadius: 14, padding: 16, marginBottom: 14 }}>
              <div style={{ fontFamily: 'var(--fhv2-mono)', fontSize: 10, letterSpacing: '1.5px', color: 'var(--fhv2-terra)', textTransform: 'uppercase', marginBottom: 8 }}>
                Invite code · share to add family
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ flex: 1, fontFamily: 'var(--fhv2-mono)', fontSize: 22, fontWeight: 700, letterSpacing: '3px', color: '#fff' }}>
                  {family.inviteCode}
                </div>
                <button onClick={handleCopyInvite} className="fhv2-btn is-primary" style={{ padding: '8px 14px' }}>
                  {copied ? 'Copied ✓' : 'Copy'}
                </button>
              </div>
              <div style={{ fontSize: 11, color: 'var(--fhv2-muted)', marginTop: 8 }}>
                Send this code so someone can join <b style={{ color: '#fff' }}>{family.name}</b>.
              </div>
            </div>
          )}

          {/* Child profiles & PIN — v2 mcard style */}
          <div className="fhv2-mlabel" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Child profiles</span>
            <button onClick={() => setShowAddDependent(true)} className="fhv2-unset" style={{ fontSize: 11, color: 'var(--fhv2-terra)', fontFamily: 'var(--fhv2-mono)', letterSpacing: '1px', cursor: 'pointer' }}>
              + ADD
            </button>
          </div>
          <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--fhv2-line)', borderRadius: 14, overflow: 'hidden', marginBottom: 14 }}>
            {dependents.length === 0 ? (
              <div style={{ padding: 14, fontSize: 12, color: 'var(--fhv2-muted)', textAlign: 'center' }}>
                No child profiles yet — add one to give your kids their own sign-in with a PIN.
              </div>
            ) : dependents.map((dep, i) => (
              <div key={dep.id} style={{ padding: 12, borderBottom: i < dependents.length - 1 ? '1px solid var(--fhv2-line)' : 'none', display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: dep.colour || 'var(--fhv2-warn)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700 }}>
                  {(dep.display_name || '?').charAt(0).toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: '#fff' }}>{dep.display_name}</div>
                  <div style={{ fontSize: 11, color: 'var(--fhv2-muted)', marginTop: 2 }}>
                    {dep.role === 'child' ? 'Child' : dep.role === 'parent' ? 'Parent' : 'Member'}
                    {dep.has_passcode ? ' · PIN set' : ' · no PIN'}
                  </div>
                </div>
                <button onClick={() => { setPinForDepId(dep.id); setPinValue(''); }} className="fhv2-btn is-ghost" style={{ padding: '6px 10px', fontSize: 11 }}>
                  {dep.has_passcode ? 'Change PIN' : 'Set PIN'}
                </button>
                <button onClick={() => removeDependent(dep.id)} className="fhv2-unset" style={{ padding: 6, cursor: 'pointer' }} aria-label="Remove">
                  <Icon name="close" size={16} color="var(--fhv2-muted)" />
                </button>
              </div>
            ))}
          </div>

          <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--fhv2-line)', borderRadius: 14, overflow: 'hidden', marginBottom: 14 }}>
            <button onClick={() => setEditing(true)} className="fhv2-unset" style={{ padding: 14, borderBottom: '1px solid var(--fhv2-line)', display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left', cursor: 'pointer' }}>
              <span style={{ fontSize: 18 }}>✏️</span>
              <div style={{ flex: 1, fontSize: 13, color: '#fff' }}>Edit profile</div>
              <Icon name="chevron_right" size={14} color="var(--fhv2-muted)" />
            </button>
            <button onClick={handleExportData} disabled={exporting} className="fhv2-unset" style={{ padding: 14, borderBottom: '1px solid var(--fhv2-line)', display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left', cursor: 'pointer' }}>
              <span style={{ fontSize: 18 }}>📤</span>
              <div style={{ flex: 1, fontSize: 13, color: '#fff' }}>{exporting ? 'Preparing…' : 'Export my data'}</div>
              <Icon name="chevron_right" size={14} color="var(--fhv2-muted)" />
            </button>
            <button onClick={() => history.push('/app/more')} className="fhv2-unset" style={{ padding: 14, borderBottom: '1px solid var(--fhv2-line)', display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left', cursor: 'pointer' }}>
              <span style={{ fontSize: 18 }}>⚙️</span>
              <div style={{ flex: 1, fontSize: 13, color: '#fff' }}>Settings</div>
              <Icon name="chevron_right" size={14} color="var(--fhv2-muted)" />
            </button>
            <button onClick={handleLogout} className="fhv2-unset" style={{ padding: 14, display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left', cursor: 'pointer', color: 'var(--fhv2-terra)' }}>
              <span style={{ fontSize: 18 }}>🚪</span>
              <div style={{ flex: 1, fontSize: 13 }}>Sign out</div>
            </button>
          </div>

          {/* Add Child Profile modal */}
          <IonModal isOpen={showAddDependent} onDidDismiss={() => setShowAddDependent(false)} initialBreakpoint={0.55} breakpoints={[0, 0.55, 0.9]}>
            <div style={{ background: 'var(--fhv2-ink)', padding: 24, minHeight: '100%', color: '#fff', fontFamily: 'var(--fhv2-sans)' }}>
              <h3 style={{ fontFamily: 'var(--fhv2-serif)', fontStyle: 'italic', fontSize: 26, marginBottom: 4 }}>Add child profile</h3>
              <p style={{ fontSize: 12, color: 'var(--fhv2-muted)', marginBottom: 18 }}>Give a child their own sign-in. You can set a PIN after.</p>

              <label style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--fhv2-muted)', display: 'block', marginBottom: 6 }}>Name</label>
              <input
                value={newDependentName}
                onChange={e => setNewDependentName(e.target.value)}
                placeholder="e.g. Tayo"
                style={{ width: '100%', padding: 12, borderRadius: 10, background: 'rgba(255,255,255,0.05)', border: '1px solid var(--fhv2-line)', color: '#fff', fontSize: 14, marginBottom: 16, outline: 'none' }}
              />

              <label style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--fhv2-muted)', display: 'block', marginBottom: 8 }}>Role</label>
              <div style={{ display: 'flex', gap: 6, marginBottom: 20 }}>
                {(['child', 'parent', 'member'] as const).map(r => (
                  <button
                    key={r}
                    onClick={() => setNewDependentRole(r)}
                    className="fhv2-unset"
                    style={{
                      flex: 1, padding: '10px 8px', borderRadius: 10, fontSize: 12, fontWeight: 600,
                      background: newDependentRole === r ? 'rgba(197,81,45,0.18)' : 'rgba(255,255,255,0.04)',
                      border: newDependentRole === r ? '1.5px solid var(--fhv2-terra)' : '1.5px solid transparent',
                      color: newDependentRole === r ? '#fff' : 'rgba(255,255,255,0.5)',
                      textTransform: 'capitalize', cursor: 'pointer',
                    }}
                  >
                    {r}
                  </button>
                ))}
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setShowAddDependent(false)} className="fhv2-btn" style={{ flex: 1, justifyContent: 'center' }}>Cancel</button>
                <button onClick={addDependent} disabled={!newDependentName.trim()} className="fhv2-btn is-primary" style={{ flex: 1, justifyContent: 'center' }}>Add</button>
              </div>
            </div>
          </IonModal>

          {/* Set PIN modal */}
          <IonModal isOpen={pinForDepId !== null} onDidDismiss={() => { setPinForDepId(null); setPinValue(''); }} initialBreakpoint={0.5} breakpoints={[0, 0.5, 0.9]}>
            <div style={{ background: 'var(--fhv2-ink)', padding: 24, minHeight: '100%', color: '#fff', fontFamily: 'var(--fhv2-sans)' }}>
              <h3 style={{ fontFamily: 'var(--fhv2-serif)', fontStyle: 'italic', fontSize: 26, marginBottom: 4 }}>Set child PIN</h3>
              <p style={{ fontSize: 12, color: 'var(--fhv2-muted)', marginBottom: 18 }}>
                {dependents.find(d => d.id === pinForDepId)?.display_name} will use this 4+ digit PIN to sign in alongside your family code.
              </p>
              <input
                type="password"
                inputMode="numeric"
                value={pinValue}
                onChange={e => setPinValue(e.target.value.replace(/\D/g, '').slice(0, 8))}
                placeholder="••••"
                autoFocus
                style={{ width: '100%', padding: 16, borderRadius: 10, background: 'rgba(255,255,255,0.05)', border: '1px solid var(--fhv2-line)', color: '#fff', fontSize: 22, textAlign: 'center', letterSpacing: '8px', marginBottom: 20, outline: 'none' }}
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => { setPinForDepId(null); setPinValue(''); }} className="fhv2-btn" style={{ flex: 1, justifyContent: 'center' }}>Cancel</button>
                <button onClick={savePin} disabled={pinValue.length < 4 || pinSaving} className="fhv2-btn is-primary" style={{ flex: 1, justifyContent: 'center', opacity: pinValue.length < 4 ? 0.5 : 1 }}>
                  {pinSaving ? 'Saving…' : 'Save PIN'}
                </button>
              </div>
            </div>
          </IonModal>

          {/* Delete account modal */}
          <IonModal isOpen={showDangerZone} onDidDismiss={() => { setShowDangerZone(false); setDeleteConfirmText(''); setDeleteError(''); }} initialBreakpoint={0.6} breakpoints={[0, 0.6, 0.9]}>
            <div style={{ background: 'var(--fhv2-ink)', padding: 24, minHeight: '100%', color: '#fff', fontFamily: 'var(--fhv2-sans)' }}>
              <h3 style={{ fontFamily: 'var(--fhv2-serif)', fontStyle: 'italic', fontSize: 26, marginBottom: 4, color: 'var(--fhv2-terra)' }}>Delete account</h3>
              <p style={{ fontSize: 13, color: 'var(--fhv2-muted)', marginBottom: 18, lineHeight: 1.5 }}>
                This is permanent. Your profile, messages, lists, calendar events — everything you've put in Famhearth — will be erased. Your family will have to remove you from their household too.
              </p>
              <div style={{ background: 'rgba(197,81,45,0.08)', border: '1px solid rgba(197,81,45,0.3)', borderRadius: 12, padding: 14, marginBottom: 16 }}>
                <div style={{ fontSize: 12, color: 'var(--fhv2-terra)', marginBottom: 8 }}>Type <b>DELETE</b> to confirm:</div>
                <input
                  value={deleteConfirmText}
                  onChange={e => setDeleteConfirmText(e.target.value)}
                  placeholder="DELETE"
                  style={{ width: '100%', padding: 12, borderRadius: 10, background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(197,81,45,0.3)', color: '#fff', fontSize: 14, outline: 'none', fontFamily: 'var(--fhv2-mono)', letterSpacing: '2px' }}
                />
              </div>
              {deleteError && (
                <div style={{ background: 'rgba(197,81,45,0.1)', border: '1px solid rgba(197,81,45,0.3)', borderRadius: 10, padding: 10, fontSize: 12, color: 'var(--fhv2-terra)', marginBottom: 12 }}>
                  {deleteError}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => { setShowDangerZone(false); setDeleteConfirmText(''); setDeleteError(''); }} className="fhv2-btn" style={{ flex: 1, justifyContent: 'center' }}>Cancel</button>
                <button onClick={handleDeleteAccount} disabled={deleteConfirmText !== 'DELETE' || deleting} className="fhv2-btn" style={{ flex: 1, justifyContent: 'center', background: 'var(--fhv2-terra)', borderColor: 'var(--fhv2-terra)', color: '#fff', opacity: deleteConfirmText !== 'DELETE' || deleting ? 0.5 : 1 }}>
                  {deleting ? 'Deleting…' : 'Delete forever'}
                </button>
              </div>
            </div>
          </IonModal>

          {/* Edit profile modal — inline so Edit profile actually works */}
          <IonModal isOpen={editing} onDidDismiss={() => setEditing(false)} initialBreakpoint={0.8} breakpoints={[0, 0.8, 1]}>
            <div style={{ background: 'var(--fhv2-ink)', padding: 24, minHeight: '100%', color: '#fff', fontFamily: 'var(--fhv2-sans)', overflowY: 'auto' }}>
              <h3 style={{ fontFamily: 'var(--fhv2-serif)', fontStyle: 'italic', fontSize: 26, marginBottom: 4 }}>Edit profile</h3>
              <p style={{ fontSize: 12, color: 'var(--fhv2-muted)', marginBottom: 20 }}>Update how you appear to the household.</p>

              {/* Avatar */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18 }}>
                <div style={{ width: 64, height: 64, borderRadius: '50%', background: selectedColour, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', color: '#fff', fontSize: 24, fontWeight: 700 }}>
                  {avatarPreview ? <img src={avatarPreview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> :
                    user?.avatar ? <img src={user.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> :
                    initials(displayName || user?.email || '?')}
                </div>
                <div style={{ flex: 1 }}>
                  <label className="fhv2-btn" style={{ cursor: 'pointer', display: 'inline-flex' }}>
                    Change photo
                    <input
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        const reader = new FileReader();
                        reader.onload = () => setAvatarPreview(reader.result as string);
                        reader.readAsDataURL(file);
                      }}
                    />
                  </label>
                  {(avatarPreview || user?.avatar) && (
                    <button onClick={() => setAvatarPreview('')} className="fhv2-unset" style={{ marginLeft: 8, fontSize: 12, color: 'var(--fhv2-terra)', cursor: 'pointer' }}>Remove</button>
                  )}
                </div>
              </div>

              {/* Name */}
              <label style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--fhv2-muted)', display: 'block', marginBottom: 6 }}>Display name</label>
              <input
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Your name"
                style={{ width: '100%', padding: 12, borderRadius: 10, background: 'rgba(255,255,255,0.05)', border: '1px solid var(--fhv2-line)', color: '#fff', fontSize: 14, outline: 'none', marginBottom: 16 }}
              />

              {/* Colour swatches */}
              <label style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--fhv2-muted)', display: 'block', marginBottom: 8 }}>Your colour</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
                {AVATAR_COLOURS.map(c => (
                  <button
                    key={c}
                    onClick={() => setSelectedColour(c)}
                    className="fhv2-unset"
                    style={{
                      width: 36, height: 36, borderRadius: '50%', background: c,
                      border: selectedColour === c ? '3px solid #fff' : '3px solid transparent',
                      cursor: 'pointer',
                    }}
                    aria-label={`Colour ${c}`}
                  />
                ))}
              </div>

              {saveError && (
                <div style={{ background: 'rgba(197,81,45,0.1)', border: '1px solid rgba(197,81,45,0.3)', borderRadius: 10, padding: 10, fontSize: 12, color: 'var(--fhv2-terra)', marginBottom: 12 }}>
                  {saveError}
                </div>
              )}

              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => { setEditing(false); setAvatarPreview(null); setDisplayName(user?.displayName || ''); setSelectedColour(user?.colour || '#a43917'); }} className="fhv2-btn" style={{ flex: 1, justifyContent: 'center' }}>
                  Cancel
                </button>
                <button onClick={handleSaveProfile} disabled={saving || !displayName.trim()} className="fhv2-btn is-primary" style={{ flex: 1, justifyContent: 'center', opacity: saving ? 0.6 : 1 }}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </IonModal>

          {/* Legacy body kept for the edit flow / delete account / invite code modals */}
          <main style={{ padding: 0 }} className="space-y-5" hidden>
            {/* Profile card */}
            <div className="rounded-2xl p-5" style={{ background: 'var(--fh-dark-card)' }}>
              {!editing ? (
                <>
                  <div className="flex items-center gap-4">
                    {user?.avatar ? (
                      <img
                        src={user.avatar}
                        alt={user.displayName || 'Profile'}
                        className="w-16 h-16 rounded-2xl flex-shrink-0"
                        style={{ objectFit: 'cover' }}
                       loading="lazy"/>
                    ) : (
                      <div
                        className="w-16 h-16 rounded-2xl flex items-center justify-center text-white text-xl font-bold flex-shrink-0"
                        style={{ background: user?.colour || 'linear-gradient(135deg, var(--fh-primary), var(--fh-primary-light))' }}
                      >
                        {user ? initials(user.displayName || user.email) : '?'}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-lg truncate" style={{ color: 'var(--fh-dark-text)', fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
                        {user?.displayName || 'You'}
                      </div>
                      <div className="text-xs mt-0.5 truncate" style={{ color: 'var(--fh-dark-text-muted)' }}>{user?.email}</div>
                      <button
                        onClick={() => {
                          setDisplayName(user?.displayName || '');
                          setSelectedColour(user?.colour || '#a43917');
                          setEditing(true);
                        }}
                        className="text-xs font-bold mt-1.5"
                        style={{ color: 'var(--fh-primary-light)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                      >
                        Edit Profile
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {/* Avatar preview + upload */}
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <button
                      type="button"
                      onClick={() => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.accept = 'image/*';
                        input.onchange = (e) => {
                          const file = (e.target as HTMLInputElement).files?.[0];
                          if (!file) return;
                          if (file.size > 2 * 1024 * 1024) { showToast('Image must be under 2MB', 'error'); return; }
                          const reader = new FileReader();
                          reader.onload = () => {
                            const img = new Image();
                            img.onload = () => {
                              const canvas = document.createElement('canvas');
                              const size = 256;
                              canvas.width = size; canvas.height = size;
                              const ctx = canvas.getContext('2d')!;
                              const min = Math.min(img.width, img.height);
                              const sx = (img.width - min) / 2, sy = (img.height - min) / 2;
                              ctx.drawImage(img, sx, sy, min, min, 0, 0, size, size);
                              setAvatarPreview(canvas.toDataURL('image/jpeg', 0.8));
                            };
                            img.src = reader.result as string;
                          };
                          reader.readAsDataURL(file);
                        };
                        input.click();
                      }}
                      style={{
                        width: 72, height: 72, borderRadius: 16, border: 'none', cursor: 'pointer',
                        position: 'relative', overflow: 'hidden', padding: 0,
                        background: selectedColour,
                      }}
                    >
                      {avatarPreview || user?.avatar ? (
                        <img src={avatarPreview || user?.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }}  loading="lazy"/>
                      ) : (
                        <span style={{ color: '#fff', fontSize: 24, fontWeight: 700 }}>
                          {displayName ? initials(displayName) : '?'}
                        </span>
                      )}
                      <div style={{
                        position: 'absolute', bottom: 0, left: 0, right: 0, height: 22,
                        background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <Icon name="photo_camera" size={12} color="var(--fh-dark-text)" />
                      </div>
                    </button>
                  </div>

                  {/* Name input */}
                  <div>
                    <label style={{ display: 'block', color: 'var(--fh-dark-text-secondary)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                      Display name
                    </label>
                    <input
                      type="text"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      placeholder="Your name"
                      style={{
                        width: '100%', padding: '12px 14px',
                        background: 'var(--fh-overlay-subtle)',
                        border: '1px solid var(--fh-dark-border)',
                        borderRadius: 12, color: 'var(--fh-dark-text)',
                        fontFamily: 'Manrope, sans-serif', fontSize: 15,
                        outline: 'none',
                      }}
                    />
                  </div>

                  {/* Colour picker */}
                  <div>
                    <label style={{ display: 'block', color: 'var(--fh-dark-text-secondary)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                      Avatar colour
                    </label>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {AVATAR_COLOURS.map(c => (
                        <button
                          key={c}
                          onClick={() => setSelectedColour(c)}
                          style={{
                            width: 36, height: 36, borderRadius: '50%',
                            background: c, border: 'none', cursor: 'pointer',
                            outline: selectedColour === c ? `3px solid ${c}` : 'none',
                            outlineOffset: 3,
                            transition: 'outline 0.15s',
                          }}
                        />
                      ))}
                    </div>
                  </div>

                  {saveError && <p style={{ color: '#ef5350', fontSize: 13, margin: 0 }}>{saveError}</p>}

                  {/* Save / Cancel buttons */}
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button
                      onClick={() => setEditing(false)}
                      style={{
                        flex: 1, padding: '12px',
                        background: 'var(--fh-overlay-light)',
                        border: '1px solid var(--fh-dark-border)',
                        borderRadius: 50, color: 'var(--fh-dark-text-secondary)',
                        fontWeight: 700, fontSize: 14, cursor: 'pointer',
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveProfile}
                      disabled={saving || !displayName.trim()}
                      style={{
                        flex: 1, padding: '12px',
                        background: 'linear-gradient(135deg, var(--fh-primary), var(--fh-primary-light))',
                        border: 'none', borderRadius: 50,
                        color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer',
                        opacity: saving || !displayName.trim() ? 0.5 : 1,
                      }}
                    >
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Family section */}
            {family ? (
              <div className="rounded-2xl overflow-hidden" style={{ background: 'var(--fh-dark-card)' }}>
                <div className="px-4 pt-4 pb-2 flex justify-between items-center">
                  <h3 className="font-bold text-sm" style={{ color: 'var(--fh-dark-text)' }}>{family.name}</h3>
                  <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: 'rgba(66,100,100,0.2)', color: '#80cbc4' }}>
                    {members.length} {members.length === 1 ? 'member' : 'members'}
                  </span>
                </div>
                <div className="px-4 pb-3 flex gap-2 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
                  {members.map((member: any, i: number) => (
                    <div key={member.id} className="flex flex-col items-center gap-1 flex-shrink-0" style={{ minWidth: 56 }}>
                      <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-xs font-bold overflow-hidden" style={{ background: member.colour || MEMBER_COLOURS[i % MEMBER_COLOURS.length] }}>
                        {member.avatar ? (
                          <img src={member.avatar} alt="" className="w-full h-full object-cover"  loading="lazy"/>
                        ) : (
                          initials(member.displayName)
                        )}
                      </div>
                      <span className="text-xs font-semibold truncate text-center w-full" style={{ color: 'var(--fh-dark-text-secondary)' }}>
                        {member.id === user?.id ? 'You' : member.displayName.split(' ')[0]}
                      </span>
                    </div>
                  ))}
                </div>
                {family.inviteCode && (
                  <div className="mx-4 mb-4 rounded-xl p-3 flex items-center justify-between" style={{ background: 'var(--fh-overlay-subtle)' }}>
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--fh-dark-text-muted)' }}>Invite code</div>
                      <div className="text-base font-extrabold tracking-widest" style={{ color: 'var(--fh-primary-light)' }}>{family.inviteCode}</div>
                    </div>
                    <button
                      onClick={handleCopyInvite}
                      className="px-3 py-1.5 rounded-lg flex items-center gap-1.5"
                      style={{ background: copied ? 'rgba(129,199,132,0.15)' : 'var(--fh-overlay-light)' }}
                    >
                      <Icon name={copied ? 'check' : 'content_copy'} size={14} color={copied ? '#81c784' : 'var(--fh-dark-text-secondary)'} />
                      <span style={{ fontSize: 11, fontWeight: 600, color: copied ? '#81c784' : 'var(--fh-dark-text-secondary)' }}>
                        {copied ? 'Copied' : 'Copy'}
                      </span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="rounded-2xl p-5 flex items-center justify-between" style={{ background: 'var(--fh-dark-card)' }}>
                <div>
                  <div className="font-semibold text-sm" style={{ color: 'var(--fh-dark-text)' }}>No family group</div>
                  <div className="text-xs" style={{ color: 'var(--fh-dark-text-muted)' }}>Set up or join a family</div>
                </div>
                <button onClick={() => history.push('/family-setup')} className="px-4 py-2 rounded-xl font-bold text-xs text-white" style={{ background: 'var(--fh-primary)' }}>Set up</button>
              </div>
            )}

            {/* Dependent profiles (members without accounts) */}
            {family && (
              <div className="rounded-2xl overflow-hidden" style={{ background: 'var(--fh-dark-card)' }}>
                <div className="px-4 pt-4 pb-2 flex justify-between items-center">
                  <h3 className="font-bold text-sm" style={{ color: 'var(--fh-dark-text)' }}>Family members without accounts</h3>
                  <button
                    onClick={() => setShowAddDependent(!showAddDependent)}
                    className="w-7 h-7 rounded-lg flex items-center justify-center"
                    style={{ background: 'rgba(164,57,23,0.15)' }}
                  >
                    <Icon name={showAddDependent ? 'close' : 'person_add'} size={16} color="#c5512d" />
                  </button>
                </div>

                {showAddDependent && (
                  <div className="px-4 pb-3 space-y-2">
                    <input
                      type="text"
                      value={newDependentName}
                      onChange={(e) => setNewDependentName(e.target.value)}
                      placeholder="Name (e.g. Sophie)"
                      className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
                      style={{ background: 'var(--fh-dark-bg)', color: 'var(--fh-dark-text)', border: '2px solid rgba(255,255,255,0.1)' }}
                    />
                    <div className="flex gap-2">
                      {['child', 'parent', 'other'].map(r => (
                        <button
                          key={r}
                          onClick={() => setNewDependentRole(r)}
                          className="px-3 py-1.5 rounded-lg text-xs font-semibold capitalize"
                          style={{
                            background: newDependentRole === r ? 'rgba(164,57,23,0.15)' : 'var(--fh-dark-bg)',
                            color: newDependentRole === r ? '#c5512d' : 'var(--fh-dark-text-secondary)',
                            border: `2px solid ${newDependentRole === r ? '#a43917' : 'rgba(255,255,255,0.1)'}`,
                          }}
                        >
                          {r}
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={addDependent}
                      disabled={!newDependentName.trim()}
                      className="w-full py-2.5 rounded-xl font-bold text-sm text-white disabled:opacity-40"
                      style={{ background: 'linear-gradient(135deg, var(--fh-primary), var(--fh-primary-light))' }}
                    >
                      Add member
                    </button>
                  </div>
                )}

                {dependents.length > 0 ? (
                  <div className="px-4 pb-4 space-y-2">
                    {dependents.map((dep: any) => (
                      <div key={dep.id} className="flex items-center gap-3 rounded-xl px-3 py-2.5" style={{ background: 'var(--fh-overlay-subtle)' }}>
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ background: dep.colour || '#426464' }}>
                          {dep.display_name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold truncate" style={{ color: 'var(--fh-dark-text)' }}>{dep.display_name}</div>
                          <div className="text-xs capitalize" style={{ color: 'var(--fh-dark-text-muted)' }}>{dep.role}</div>
                        </div>
                        <button
                          onClick={async () => {
                            const code = prompt(`Set passcode for ${dep.display_name} (min 4 chars):`);
                            if (!code || code.length < 4) { if (code !== null) showToast('Passcode must be at least 4 characters', 'error'); return; }
                            try {
                              await api.put(`/family/dependents/${dep.id}/passcode`, { passcode: code });
                              showToast(`Passcode set for ${dep.display_name}`, 'success');
                            } catch { showToast('Failed to set passcode', 'error'); }
                          }}
                          className="rounded-lg px-2.5 py-1.5 text-xs font-bold"
                          style={{ background: 'rgba(129,199,132,0.1)', color: '#81c784', border: 'none', cursor: 'pointer' }}
                        >
                          {dep.passcode_hash ? 'Reset PIN' : 'Set PIN'}
                        </button>
                        <button
                          onClick={() => removeDependent(dep.id)}
                          className="w-7 h-7 rounded-lg flex items-center justify-center"
                          style={{ background: 'rgba(239,83,80,0.08)' }}
                        >
                          <Icon name="close" size={14} color="rgba(239,83,80,0.5)" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : !showAddDependent ? (
                  <div className="px-4 pb-4">
                    <p className="text-xs" style={{ color: 'var(--fh-dark-text-muted)' }}>
                      Add children or others who don't have their own account.
                    </p>
                  </div>
                ) : null}
              </div>
            )}

            {/* Quick links */}
            <div className="space-y-1.5">
              <p className="text-xs font-bold uppercase tracking-widest px-1 mb-1" style={{ color: 'rgba(255,255,255,0.3)' }}>Quick links</p>
              <button
                onClick={() => {
                  // Placeholder — will link to App Store once live
                  openExternal('mailto:feedback@famhearth.com?subject=FamHearth Feedback', '_blank');
                }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl active:scale-[0.98] transition-transform"
                style={{ background: 'var(--fh-dark-card)' }}
              >
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#ff8a6515' }}>
                  <Icon name="star" size={18} color="#ff8a65" />
                </div>
                <span className="flex-1 text-sm font-semibold text-left" style={{ color: 'var(--fh-dark-text)' }}>Rate FamHearth</span>
                <Icon name="chevron_right" size={16} color="rgba(255,255,255,0.15)" />
              </button>
            </div>

            {/* Account & Privacy */}
            <div className="space-y-1.5">
              <p className="text-xs font-bold uppercase tracking-widest px-1 mb-1" style={{ color: 'rgba(255,255,255,0.3)' }}>Account & Privacy</p>
              <button
                onClick={() => history.push('/app/privacy')}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl active:scale-[0.98] transition-transform"
                style={{ background: 'var(--fh-dark-card)' }}
              >
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(179,157,219,0.1)' }}>
                  <Icon name="privacy_tip" size={18} color="#b39ddb" />
                </div>
                <span className="flex-1 text-sm font-semibold text-left" style={{ color: 'var(--fh-dark-text)' }}>Privacy Policy</span>
                <Icon name="chevron_right" size={16} color="rgba(255,255,255,0.15)" />
              </button>
              <button
                onClick={handleExportData}
                disabled={exporting}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl active:scale-[0.98] transition-transform"
                style={{ background: 'var(--fh-dark-card)', opacity: exporting ? 0.6 : 1 }}
              >
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(79,195,247,0.1)' }}>
                  <Icon name="download" size={18} color="#4fc3f7" />
                </div>
                <span className="flex-1 text-sm font-semibold text-left" style={{ color: 'var(--fh-dark-text)' }}>
                  {exporting ? 'Exporting...' : 'Download My Data'}
                </span>
                <Icon name="chevron_right" size={16} color="rgba(255,255,255,0.15)" />
              </button>
            </div>

            {/* Sign out */}
            <button
              onClick={handleLogout}
              className="w-full py-3 rounded-xl font-bold text-sm active:scale-95 transition-transform"
              style={{ background: 'rgba(239,83,80,0.08)', color: '#ef5350' }}
            >
              Sign Out
            </button>

            {/* Danger Zone — collapsed by default */}
            <div className="mt-6">
              <button
                onClick={() => setShowDangerZone(!showDangerZone)}
                className="w-full flex items-center justify-between px-4 py-3 rounded-xl"
                style={{ background: 'rgba(239,83,80,0.05)', border: '1px solid rgba(239,83,80,0.1)' }}
              >
                <span className="text-xs font-semibold" style={{ color: 'rgba(239,83,80,0.5)' }}>Danger Zone</span>
                <Icon name={showDangerZone ? 'expand_less' : 'expand_more'} size={16} color="rgba(239,83,80,0.3)" />
              </button>
              {showDangerZone && (
                <div className="mt-2 rounded-xl p-4 space-y-3" style={{ background: 'rgba(239,83,80,0.05)', border: '1px solid rgba(239,83,80,0.1)' }}>
                  <p className="text-xs" style={{ color: 'var(--fh-dark-text-secondary)' }}>
                    This will permanently delete your account and all associated data. This cannot be undone.
                  </p>
                  <input
                    type="text"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    placeholder='Type "DELETE" to confirm'
                    className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                    style={{ background: 'var(--fh-dark-bg)', color: '#ef5350', border: '1px solid rgba(239,83,80,0.2)' }}
                  />
                  {deleteError && <p className="text-xs" style={{ color: '#ef5350' }}>{deleteError}</p>}
                  <button
                    onClick={handleDeleteAccount}
                    disabled={deleteConfirmText !== 'DELETE' || deleting}
                    className="w-full py-2.5 rounded-xl text-sm font-bold disabled:opacity-30"
                    style={{ background: 'rgba(239,83,80,0.15)', color: '#ef5350' }}
                  >
                    {deleting ? 'Deleting...' : 'Delete My Account'}
                  </button>
                </div>
              )}
            </div>

            <p className="text-center text-xs pb-4" style={{ color: 'rgba(255,255,255,0.2)' }}>FamHearth v0.2</p>
          </main>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default ProfilePage;
